import { Factory } from "@/data/dao/Factory";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import { PrintDao } from "@/data/dao/service/PrintDao";
import { PrintJobState } from "@/data/model/Enums";
import type { IPrintJob } from "@/data/model/Print";
import type { IProductionSite } from "@/data/model/ProductionSite";
import { role } from "@/data/util/Config";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";

import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";

import { i18n, isInRole } from "@/portal/Trans";
import { BaseTabbedController } from "@/portal/controller/BaseTabbedController";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { ProductionSiteForm } from "@/portal/forms/ProductionSiteForm";
import { TastierGlobal } from "@/portal/globalAccess";
import { UiNotification } from "@/portal/ui/Notification";

export class ProductionSiteController extends BaseTabbedController<IProductionSite> {
    private readonly productionSiteForm: ProductionSiteForm;
    private printJobTable?: DataTable<IPrintJob>;

    constructor(element: JQuery) {
        super(element, Factory.getInstance<CatalogDao>(CatalogDao).productionSiteDataSource);

        this.productionSiteForm = new ProductionSiteForm(this.formContentByTabName("productionsiteedit"));
        if (!isInRole(role.Admin) && !isInRole(role.Manager)) this.productionSiteForm.setReadOnly(true);

        this.registerModelForm(this.productionSiteForm);

        this.onActivateTab = async (newTab) => {
            if (newTab === "debug")
                await this.showDebugArea();
        };
    }

    protected onModelLoaded(m?: IProductionSite) {
        if (m === undefined) {
            this.disableTabs(["debug"]);
            const vw = $("#kt_header > #kt_header_menu_wrapper");
            if (vw.find("> .selectedlocation").length === 0)
                vw.append(
                    '<div class="selectedlocation">' +
                        Format.htmlEscape(TastierGlobal.getLocationSwitch()!.getCurrentLocationName()) +
                        "</div>"
                );
        } else this.enableTabs(["debug"]);

        this.productionSiteForm.setData(m);
    }

    private async showDebugArea() {
        if (this.printJobTable !== undefined) {
            await this.printJobTable.loadData();
            return;
        }

        const debugContainer = this.formContentByTabName("debug");
        const testOrderPrintBtn = debugContainer
            .find("#testprintbtn")
            .unbind("click")
            .bind("click", (e) => {
                e.preventDefault();

                testOrderPrintBtn.addClass(style.buttonLoaderClass).prop("disabled", true);

                Factory.getInstance<CatalogDao>(CatalogDao)
                    .testPrintOrder(this.entityId!)
                    .then(async () => {
                        testOrderPrintBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                        UiNotification.showSuccess(i18n("productionSite.testPrintSent"));
                        await this.printJobTable!.loadData();
                    })
                    .catch((ex) => {
                        logError(ex);
                        testOrderPrintBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                        UiNotification.showException(i18n("productionSite.failedToSendTestPrint"));
                    });
            });

        const testPrintInterimBillBtn = debugContainer
            .find("#test-print-interim-bill-btn")
            .unbind("click")
            .bind("click", (e) => {
                e.preventDefault();

                testPrintInterimBillBtn.addClass(style.buttonLoaderClass).prop("disabled", true);

                Factory.getInstance<CatalogDao>(CatalogDao)
                    .testPrintInterimBill(this.entityId!)
                    .then(async () => {
                        testPrintInterimBillBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                        UiNotification.showSuccess(i18n("productionSite.testPrintSent"));
                        await this.printJobTable!.loadData();
                    })
                    .catch((ex) => {
                        logError(ex);
                        testPrintInterimBillBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                        UiNotification.showException(i18n("productionSite.failedToSendTestPrint"));
                    });
            });

        const testPrintPaymentReceiptBtn = debugContainer
            .find("#test-print-payment-receipt-btn")
            .unbind("click")
            .bind("click", (e) => {
                e.preventDefault();

                testPrintPaymentReceiptBtn.addClass(style.buttonLoaderClass).prop("disabled", true);

                Factory.getInstance<CatalogDao>(CatalogDao)
                    .testPrintBillPaymentReceipt(this.entityId!)
                    .then(async () => {
                        testPrintPaymentReceiptBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                        UiNotification.showSuccess(i18n("productionSite.testPrintSent"));
                        await this.printJobTable!.loadData();
                    })
                    .catch((ex) => {
                        logError(ex);
                        testPrintPaymentReceiptBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                        UiNotification.showException(i18n("productionSite.failedToSendTestPrint"));
                    });
            });

        const testPrintOrderReceiptBtn = debugContainer
            .find("#test-print-order-receipt-btn")
            .unbind("click")
            .bind("click", (e) => {
                e.preventDefault();

                testPrintOrderReceiptBtn.addClass(style.buttonLoaderClass).prop("disabled", true);

                Factory.getInstance<CatalogDao>(CatalogDao)
                    .testPrintOrderPaymentReceipt(this.entityId!)
                    .then(async () => {
                        testPrintOrderReceiptBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                        UiNotification.showSuccess(i18n("productionSite.testPrintSent"));
                        await this.printJobTable!.loadData();
                    })
                    .catch((ex) => {
                        logError(ex);
                        testPrintOrderReceiptBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                        UiNotification.showException(i18n("productionSite.failedToSendTestPrint"));
                    });
            });
        
        const dao = Factory.getInstance<PrintDao>(PrintDao);
        const tableConfig: IDataTableConfig<IPrintJob> = {
            columnMapping: [
                {
                    name: i18n("general.fields.created"),
                    property: "created",
                    styles: { width: "17%", "padding-left": "0px !important" },
                    formatter: (_, p) => DateTime.toPreciseDateTimeString(p),
                    sortable: false,
                },
                {
                    name: i18n("general.fields.state"),
                    property: "state",
                    sortable: false,
                    styles: { width: "17%" },
                    formatter: (m, p) => {
                        let styleClass = "kt-badge--primary";
                        switch (p) {
                            case PrintJobState.Pending:
                                styleClass = "kt-badge--info";
                                break;
                            case PrintJobState.Printed:
                                styleClass = "kt-badge--success";
                                break;
                            case PrintJobState.Failed:
                                styleClass = "kt-badge--danger";
                                break;
                            case PrintJobState.Unconfirmed:
                                styleClass = "kt-badge--warning";
                                break;
                        }

                        return (
                            '<div class="kt-badge ' +
                            styleClass +
                            ' kt-badge--inline" ' +
                            (m.failReason !== undefined && p === PrintJobState.Failed
                                ? 'title="' + m.failReason + '"'
                                : "") +
                            ">" +
                            i18n("enums.PrintJobState." + PrintJobState[p]) +
                            "</div>"
                        );
                    },
                },
                {
                    name: i18n("productionSite.fields.backupPrint"),
                    property: "isBackupPrint",
                    styles: { width: "15%", "text-align": "center" },
                    sortable: false,
                    formatter: (_, p) => {
                        return p ? '<i class="fas fa-check"></i>' : "";
                    },
                },
                {
                    name: i18n("productionSite.fields.forwarded"),
                    property: "forwardedTo",
                    styles: { width: "16%", "text-align": "center" },
                    sortable: false,
                    formatter: (_, p) => {
                        return p ? '<i class="fas fa-check"></i>' : "";
                    },
                },
                {
                    name: i18n("general.fields.name"),
                    property: "name",
                    sortable: false,
                },
                {
                    name: "",
                    property: "id",
                    styles: { width: "110px", "text-align": "right", "padding-right": "0px !important" },
                    sortable: false,
                    formatter: (m, p) => {
                        if (m.state === PrintJobState.Pending) return "";

                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-trash"></i></a></span>'
                        )
                            .data("model", p)
                            .click((e) => {
                                e.preventDefault();

                                dao.deleteJob($(e.currentTarget).data("model"))
                                    .then(() => {
                                        this.printJobTable!.loadData();
                                    })
                                    .catch((ex) => {
                                        logError(ex);
                                        UiNotification.showException(i18n("productionSite.failedDeleteJob"));
                                    });
                            });
                    },
                },
            ],
            filterProvider: {
                getFilter: () => ({ productionSiteId: this.entityId }) as any,
                setFilter: () => {},
            },
            paginated: false,
            autoload: false,
            defaultSortAsc: false,
            locationDependend: true,
            noDatatext: i18n("productionSite.noJobs"),
            defaultSortProperty: "date",
        };

        this.printJobTable = new DataTable<IPrintJob>(debugContainer.find(".kt-datatable"), dao, tableConfig);
        this.printJobTable.onClick((m) => {
            if (m.state !== PrintJobState.Pending) return;

            UiNotification.confirm(
                i18n("productionSite.reallyCancel"),
                i18n("productionSite.cancelJob"),
                () => {
                    dao.cancelJob(m.id)
                        .then(() => {
                            this.printJobTable!.loadData();
                        })
                        .catch((ex) => {
                            logError(ex);
                            window.setTimeout(() => {
                                UiNotification.showException(i18n("productionSite.failedCancelJob"));
                            }, 200);
                        });
                },
                i18n("productionSite.cancelJob")
            );
        });

        await this.printJobTable.loadData();
    }
}
